import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import {
  AUTH_UNREACHABLE_MESSAGE,
  isAuthConnectionError,
} from '../../shared/auth/auth-connection-error';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { AuthDivider } from './AuthDivider';
import { AuthLayout } from './AuthLayout';
import { GoogleButton } from './GoogleButton';
import s from './LoginPage.module.css';
import { loginSchema, type LoginInput } from './schemas';

interface LocationState {
  from?: string;
  /** Set by the reset-password flow so a completed reset lands with a receipt. */
  notice?: string;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const notice = (location.state as LocationState | null)?.notice ?? null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginInput): Promise<void> {
    setSubmitError(null);
    const { error } = await supabase.auth.signInWithPassword(values);
    if (error !== null) {
      // Supabase's raw message for an unreachable server is transport jargon
      // ("Auth request timed out"), which reads as though the account is at
      // fault. Say plainly that it is not.
      setSubmitError(isAuthConnectionError(error) ? AUTH_UNREACHABLE_MESSAGE : error.message);
      return;
    }
    const state = location.state as LocationState | null;
    const destination = state?.from ?? '/app';
    navigate(destination, { replace: true });
  }

  return (
    <AuthLayout
      pitchTitle={
        <>
          Welcome <em>back.</em> Your next mock is one focused hour away.
        </>
      }
      pitchBody="Log in to resume practice, review past attempts, and pick up exactly where your tutor left off."
    >
      <span className={s.eyebrow}>Sign in</span>
      <h1 className={s.title}>Log in</h1>
      <p className={s.subtitle}>
        New here? <Link to="/signup">Create an account</Link>.
      </p>

      {notice !== null ? (
        <StatusBanner tone="success" className={s.notice ?? ''}>
          {notice}
        </StatusBanner>
      ) : null}

      <GoogleButton label="Sign in" />
      <AuthDivider label="or continue with email" />

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.form}
      >
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            placeholder="you@institute.edu"
            invalid={errors.email !== undefined}
            aria-describedby={errors.email ? 'email-error' : undefined}
            {...register('email')}
          />
          <FormError id="email-error" message={errors.email?.message} />
        </div>

        <div>
          <div className={s.passwordRow}>
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className={s.forgot}>
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            enterKeyHint="go"
            placeholder="••••••••"
            invalid={errors.password !== undefined}
            aria-describedby={errors.password ? 'password-error' : undefined}
            {...register('password')}
          />
          <FormError id="password-error" message={errors.password?.message} />
        </div>

        {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Log in
        </Button>

        <p className={s.studentHint}>
          Student at an institute? Sign in with the login link your institute shared with you.
        </p>
      </form>
    </AuthLayout>
  );
}
